import { AnimatePresence, motion } from 'framer-motion'
import type { GameGoldMiner } from '../../../../services/games/types'
import { formatDecimalPlaces } from '../../../../helpers/functions/format-decimal-places'

interface Props {
  /** picareta batendo — do toque até o servidor responder (mín. 1s) */
  mining: boolean
  /** prêmio do 1x, sai do monte de ouro */
  prize: GameGoldMiner | null
}

// Centro do monte de ouro no fundo, em coordenadas do palco (440×498)
const PILE_X = 220
const PILE_Y = 268

const NUGGETS = Array.from({ length: 9 }, (_, i) => {
  const angle = (-160 + (i * 140) / 8) * (Math.PI / 180)
  const dist = 38 + (i % 3) * 14
  return { x: Math.cos(angle) * dist, y: Math.sin(angle) * dist, d: (i % 4) * 0.06 }
})

function Pickaxe() {
  return (
    <svg viewBox="0 0 64 64" width="88" height="88" aria-hidden>
      {/* cabo */}
      <rect x="29" y="14" width="6" height="46" rx="3" fill="#8B5A2B" stroke="#4a2c10" strokeWidth="1.5" />
      <rect x="30.5" y="16" width="2" height="42" rx="1" fill="#b27a45" />
      {/* cabeça */}
      <path
        d="M4 20 Q32 2 60 20 L56 24 Q32 10 8 24 Z"
        fill="#cfd8dc"
        stroke="#455a64"
        strokeWidth="2"
        strokeLinejoin="round"
      />
      <rect x="27" y="10" width="10" height="10" rx="2" fill="#78909c" stroke="#37474f" strokeWidth="1.5" />
    </svg>
  )
}

export function MiningScene({ mining, prize }: Props) {
  return (
    <div
      className="pointer-events-none absolute z-10"
      style={{ left: PILE_X, top: PILE_Y, width: 0, height: 0 }}
    >
      {/* brilho do monte enquanto minera */}
      <AnimatePresence>
        {mining && (
          <motion.div
            key="glow"
            className="absolute rounded-full"
            style={{
              left: -70,
              top: -40,
              width: 140,
              height: 80,
              background:
                'radial-gradient(ellipse, rgba(255,214,90,0.55), rgba(255,214,90,0) 70%)',
            }}
            initial={{ opacity: 0 }}
            animate={{ opacity: [0.5, 1, 0.5] }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.45, repeat: Infinity }}
          />
        )}
      </AnimatePresence>

      {/* picareta batendo */}
      <AnimatePresence>
        {mining && (
          <motion.div
            key="pickaxe"
            className="absolute"
            // cabo apoiado à direita do monte (pivô no pé do cabo); a ponta
            // desce da esquerda-alto até acertar o topo do ouro
            style={{ left: 18, top: -94, transformOrigin: '50% 95%' }}
            initial={{ opacity: 0, rotate: -30 }}
            animate={{ opacity: 1, rotate: [-25, -82, -25] }}
            exit={{ opacity: 0 }}
            transition={{
              rotate: { duration: 0.45, repeat: Infinity, ease: 'easeIn' },
              opacity: { duration: 0.15 },
            }}
          >
            <Pickaxe />
          </motion.div>
        )}
      </AnimatePresence>

      {/* pepitas voando a cada batida */}
      {mining &&
        NUGGETS.map((n, i) => (
          <motion.span
            key={i}
            className="absolute block rounded-full"
            style={{
              left: -4,
              top: -6,
              width: 9 + (i % 3) * 3,
              height: 9 + (i % 3) * 3,
              background: i % 3 ? '#ffd24a' : '#fff3b0',
              boxShadow: '0 0 6px rgba(255,200,50,0.9)',
            }}
            initial={{ x: 0, y: 0, opacity: 0, scale: 0.4 }}
            animate={{ x: [0, n.x], y: [0, n.y, n.y + 22], opacity: [0, 1, 0], scale: [0.4, 1, 0.7] }}
            transition={{ duration: 0.45, repeat: Infinity, delay: n.d, ease: 'easeOut' }}
          />
        ))}

      {/* prêmio do 1x saindo do monte — entrada em CSS (sem piscada no fim) */}
      {prize && !mining && (
        <div
          key={`${prize.giftIdEarning}-${prize.newCoins}`}
          className="gm-prize absolute flex flex-col items-center"
          style={{ left: -60, width: 120, top: -135 }}
        >
          <div
            className="absolute rounded-full"
            style={{
              top: -14,
              width: 110,
              height: 110,
              background:
                'radial-gradient(circle, rgba(255,230,120,0.75), rgba(255,200,60,0) 68%)',
            }}
          />
          <img
            src={prize.imgGift || prize.imgGiftFrame}
            alt={prize.giftName}
            className="relative h-[76px] w-[76px] object-contain drop-shadow-[0_4px_8px_rgba(0,0,0,0.5)]"
          />
          <div className="relative mt-1 flex items-center gap-1 rounded-full border border-yellow-300/80 bg-gradient-to-b from-amber-700 to-amber-900 px-3 py-0.5 shadow-lg">
            {prize.quantity > 1 && (
              <span className="text-[12px] font-black text-white">×{prize.quantity}</span>
            )}
            <span className="text-[14px] font-extrabold text-yellow-200">
              +{formatDecimalPlaces(prize.valueGift)}
            </span>
          </div>
        </div>
      )}
    </div>
  )
}
